// Svelte-free by design, so a `node:test` file can exercise it.

/**
 * Il bilancio depositato, lato interfaccia: chi può fare cosa, come si
 * scrivono gli importi e come si mostra su che cosa poggia un valore.
 *
 * I conti li fa il server (src/domain/bilancio/*): gli importi arrivano come
 * stringhe decimali esatte e qui non passano mai per un numero in virgola
 * mobile, così un euro letto nel bilancio è lo stesso euro mostrato a video.
 * Solo gli indici (%, volte, giorni) arrivano già come numeri arrotondati.
 */

// ── Ruoli ─────────────────────────────────────────────────────────────

/**
 * Copia speculare delle costanti di src/api/bilancio-roles.js:
 * test/frontend-bilancio.test.js le tiene allineate. Il server resta
 * l'autorità; qui servono solo a non offrire ciò che verrebbe rifiutato.
 */
export const BILANCIO_READ_ROLES = Object.freeze(['admin', 'operator', 'readonly', 'commercialista']);
export const BILANCIO_WRITE_ROLES = Object.freeze(['admin', 'operator']);
export const BILANCIO_REPLACE_ROLES = Object.freeze(['admin']);
export const BILANCIO_DELETE_ROLES = Object.freeze(['admin']);
export const BILANCIO_RAW_READ_ROLES = Object.freeze(['admin', 'operator', 'readonly', 'commercialista']);

/**
 * Cosa può fare con i bilanci chi ha questo ruolo nell'azienda attiva (il
 * ruolo che restituisce `/capabilities`). Un ruolo sconosciuto, o non ancora
 * arrivato, non può niente.
 *
 * @param {string|null|undefined} role
 */
export function bilancioPermissions(role) {
  return {
    read: BILANCIO_READ_ROLES.includes(role),
    upload: BILANCIO_WRITE_ROLES.includes(role),
    replace: BILANCIO_REPLACE_ROLES.includes(role),
    remove: BILANCIO_DELETE_ROLES.includes(role),
    rawRead: BILANCIO_RAW_READ_ROLES.includes(role),
  };
}

// ── Il file ───────────────────────────────────────────────────────────

/** Lo stesso limite del server (MAX_FILE_BYTES): 5 MB di file. */
export const MAX_BILANCIO_BYTES = 5 * 1024 * 1024;

/** Le estensioni che il selettore propone: l'istanza XBRL depositata. */
export const BILANCIO_ACCEPT = '.xbrl,.xml';

/**
 * Perché un file scelto non viene nemmeno mandato al server, o null se si può
 * mandare. Il controllo vero lo fa il server; questo evita di caricare 30 MB
 * di PDF per sentirsi dire che non è un bilancio.
 *
 * @param {{ name?: string, size?: number }|null|undefined} file
 * @returns {'missing'|'empty'|'tooLarge'|'extension'|null}
 */
export function fileProblem(file) {
  if (!file) return 'missing';
  if (!file.size) return 'empty';
  if (file.size > MAX_BILANCIO_BYTES) return 'tooLarge';
  if (!/\.(xbrl|xml)$/i.test(file.name || '')) return 'extension';
  return null;
}

/** Il contenuto di un data: URL senza il prefisso, cioè il base64 che l'API vuole. */
export function base64FromDataUrl(dataUrl) {
  const comma = typeof dataUrl === 'string' ? dataUrl.indexOf(',') : -1;
  return comma >= 0 ? dataUrl.slice(comma + 1) : '';
}

/** 245760 → «240 kB»; per mostrare la dimensione del file. */
export function formatFileSize(bytes) {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} byte`;
  if (bytes < 1024 * 1024) return `${formatNumber(bytes / 1024, 0)} kB`;
  return `${formatNumber(bytes / (1024 * 1024), 1)} MB`;
}

/** Il file archiviato è a posto; altrimenti va ricaricato per ripristinarlo. */
export function fileNeedsRepair(bilancio) {
  return Boolean(bilancio) && bilancio.fileStatus !== undefined && bilancio.fileStatus !== 'ok';
}

// ── Numeri esatti ─────────────────────────────────────────────────────

const DECIMAL = /^([+-])?(\d+)(?:\.(\d+))?$/;

/**
 * Una stringa decimale come intero scalato: «-12.5» → { n: -125n, s: 1 }.
 * Null per tutto ciò che non è un decimale semplice.
 */
function parseDecimal(value) {
  const text = typeof value === 'number' && Number.isFinite(value) ? numberToPlain(value) : value;
  if (typeof text !== 'string') return null;
  const match = DECIMAL.exec(text.trim());
  if (!match) return null;
  const [, sign, whole, fraction = ''] = match;
  const n = BigInt(`${whole}${fraction}`);
  return { n: sign === '-' ? -n : n, s: fraction.length };
}

/** Un numero JavaScript in notazione semplice (mai «1e-7»). */
function numberToPlain(value) {
  const text = String(value);
  if (!/e/i.test(text)) return text;
  return value.toFixed(20).replace(/\.?0+$/, '');
}

function rescale(a, s) {
  return a.n * 10n ** BigInt(s - a.s);
}

/** Il segno di un decimale esatto: -1, 0 o 1; null se non è un decimale. */
export function decimalSign(value) {
  const parsed = parseDecimal(value);
  if (!parsed) return null;
  if (parsed.n === 0n) return 0;
  return parsed.n < 0n ? -1 : 1;
}

/**
 * Metà dell'ampiezza di un intervallo, esatta: (max − min) / 2. È il «± X» di
 * un valore stimato, che sta in mezzo ai due estremi.
 *
 * @returns {string|null} stringa decimale, o null se un estremo non è leggibile
 */
export function halfWidth(min, max) {
  const low = parseDecimal(min);
  const high = parseDecimal(max);
  if (!low || !high) return null;
  const s = Math.max(low.s, high.s);
  const span = rescale(high, s) - rescale(low, s);
  const magnitude = span < 0n ? -span : span;
  // Metà di un intero dispari scalato a s cifre è esatta a s + 1 cifre.
  if (magnitude % 2n === 0n) return plain(magnitude / 2n, s);
  return plain(magnitude * 5n, s + 1);
}

function plain(n, s) {
  const negative = n < 0n;
  const digits = (negative ? -n : n).toString().padStart(s + 1, '0');
  const whole = s ? digits.slice(0, -s) : digits;
  const fraction = s ? digits.slice(-s) : '';
  return `${negative ? '-' : ''}${whole}${fraction ? `.${fraction}` : ''}`;
}

/** «1234567» → «1.234.567»: il separatore delle migliaia, sempre. */
function groupThousands(whole) {
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/**
 * Un decimale nella notazione italiana, con almeno `minFraction` cifre dopo la
 * virgola e senza perderne nessuna: «1234.5» → «1.234,50». Il separatore delle
 * migliaia c'è sempre, anche a quattro cifre, così le colonne si leggono.
 *
 * @param {string|number} value
 * @param {{ minFraction?: number, signed?: boolean }} [opts] `signed` mette il «+» davanti ai positivi
 * @returns {string|null}
 */
export function formatDecimal(value, { minFraction = 0, signed = false } = {}) {
  const parsed = parseDecimal(value);
  if (!parsed) return null;
  const negative = parsed.n < 0n;
  const digits = (negative ? -parsed.n : parsed.n).toString().padStart(parsed.s + 1, '0');
  const whole = (parsed.s ? digits.slice(0, -parsed.s) : digits).replace(/^0+(?=\d)/, '');
  let fraction = parsed.s ? digits.slice(-parsed.s) : '';
  if (fraction.length < minFraction) fraction = fraction.padEnd(minFraction, '0');
  const zero = parsed.n === 0n;
  const sign = zero ? '' : negative ? '-' : signed ? '+' : '';
  return `${sign}${groupThousands(whole)}${fraction ? `,${fraction}` : ''}`;
}

/**
 * Un decimale arrotondato al centesimo, la metà lontano dallo zero (come le
 * fatture arrotondano): «0.125» → «0.13», «-0.125» → «-0.13».
 */
function roundToCents(value) {
  const parsed = parseDecimal(value);
  if (!parsed || parsed.s <= 2) return value;
  const drop = 10n ** BigInt(parsed.s - 2);
  const magnitude = parsed.n < 0n ? -parsed.n : parsed.n;
  let cents = magnitude / drop;
  if ((magnitude % drop) * 2n >= drop) cents += 1n;
  return plain(parsed.n < 0n ? -cents : cents, 2);
}

/**
 * Un importo in euro: «-1234.5» → «-1.234,50 €», sempre con due decimali. Il
 * server li manda già al centesimo; qualunque cifra oltre il centesimo è
 * arrotondata qui, la metà lontano dallo zero, perché un euro non ne ha.
 *
 * @param {string|number} value
 * @param {{ signed?: boolean }} [opts]
 * @returns {string|null} null se il valore non è un importo leggibile
 */
export function formatMoney(value, { signed = false } = {}) {
  const text = formatDecimal(roundToCents(value), { minFraction: 2, signed });
  return text === null ? null : `${text} €`;
}

/** Un numero arrotondato a `places` cifre, nella notazione italiana. */
export function formatNumber(value, places) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return formatDecimal(value.toFixed(places), { minFraction: places });
}

/** Le cifre mostrate per ogni unità di misura degli indici. */
const RATIO_PLACES = Object.freeze({ '%': 2, x: 2, giorni: 1 });

/**
 * Il valore di un indice nella sua unità: «€» importo esatto, «%» con due
 * decimali, «x» (volte) con due, «giorni» con uno.
 *
 * @param {string|number|null} value
 * @param {'€'|'%'|'x'|'giorni'} unit
 * @param {{ signed?: boolean }} [opts] `signed` per una variazione: «+» davanti ai positivi
 * @returns {string|null}
 */
export function formatRatioValue(value, unit, { signed = false } = {}) {
  if (value === null || value === undefined) return null;
  if (unit === '€') return formatMoney(value, { signed });
  const number = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(number)) return null;
  const places = RATIO_PLACES[unit] ?? 2;
  const text = formatDecimal(number.toFixed(places), { minFraction: places, signed });
  if (text === null) return null;
  if (unit === '%') return `${text}%`;
  if (unit === 'giorni') return `${text} giorni`;
  return text;
}

/** La variazione rispetto all'anno prima, in percentuale e con il segno: «+12,40%». */
export function formatVariation(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return formatRatioValue(value, '%', { signed: true });
}

// ── Su che cosa poggia un valore ──────────────────────────────────────

/**
 * Le basi di un indice, dalla più forte alla più debole (§4.4). La forma breve
 * è quella dei badge: `certo` non ne ha bisogno, `stima` e `ipotesi` sì.
 */
export const BASIS_KIND = Object.freeze({
  certo: 'certo',
  stimato: 'stima',
  ipotesi_voci_non_esposte_zero: 'ipotesi',
});

/**
 * Come mostrare il valore di un indice (o di una cifra di bilancio del
 * riscontro): un valore che non è certo non è mai mostrato come un dato.
 *
 *   - `nd`: nessun valore, con il motivo («n.d.»);
 *   - `certo`: il valore e basta;
 *   - `stima`: il valore con «stima ± X» (per gli importi) e l'intervallo;
 *   - `ipotesi`: il valore con il badge «ipotesi» e, se c'è, l'intervallo.
 *
 * @param {{ value?: string|number|null, basis?: string|null, interval?: {min, max}|null,
 *   tolerance?: string|null, reason?: string|null }|null|undefined} result
 * @param {'€'|'%'|'x'|'giorni'} unit
 * @param {{ signed?: boolean }} [opts] `signed` per una variazione
 * @returns {{ kind: 'nd'|'certo'|'stima'|'ipotesi', text: string|null, plusMinus: string|null,
 *   range: { min: string, max: string }|null, reason: string|null }}
 */
export function describeValue(result, unit, { signed = false } = {}) {
  const reason = result?.reason ?? null;
  const kind = BASIS_KIND[result?.basis];
  const text = kind ? formatRatioValue(result.value, unit, { signed }) : null;
  if (!kind || text === null) return { kind: 'nd', text: null, plusMinus: null, range: null, reason };

  let range = null;
  let plusMinus = null;
  const interval = result.interval
    ?? (unit === '€' && result.tolerance && decimalSign(result.tolerance) !== 0 && result.value !== null
      ? toleranceInterval(result.value, result.tolerance) : null);
  if (interval) {
    const min = formatRatioValue(interval.min, unit, { signed });
    const max = formatRatioValue(interval.max, unit, { signed });
    // Un intervallo più stretto delle cifre mostrate («tra 6,09 e 6,09») non
    // dice niente: resta il badge, che dice comunque che è una stima.
    if (min !== null && max !== null && min !== max) range = { min, max };
    // Il «± X» solo per gli importi: un rapporto tra due intervalli non è
    // simmetrico attorno al valore, e un «±» lo farebbe credere.
    if (unit === '€') {
      const half = halfWidth(interval.min, interval.max);
      if (half !== null && decimalSign(half) !== 0) plusMinus = formatMoney(half);
    }
  }
  return { kind, text, plusMinus, range, reason };
}

/** [valore − tolleranza, valore + tolleranza], esatto. */
function toleranceInterval(value, tolerance) {
  const v = parseDecimal(value);
  const t = parseDecimal(tolerance);
  if (!v || !t) return null;
  const s = Math.max(v.s, t.s);
  return { min: plain(rescale(v, s) - rescale(t, s), s), max: plain(rescale(v, s) + rescale(t, s), s) };
}

/**
 * Come mostrare una cella dello stato patrimoniale o del conto economico.
 *
 *   - `heading`: un titolo senza valore;
 *   - `reported` / `computed`: il valore (esposto, o somma esatta di voci note);
 *   - `solved`: «ricavata per differenza», con «± X» quando gli addendi sono arrotondati;
 *   - `unknown`: «non esposta» (mai uno zero presunto); `ipotesiZero` dice che
 *     solo gli indici «ipotesi» la considerano zero;
 *   - `nil`: dichiarata vuota dal bilancio.
 *
 * @param {object|null} cell BilancioStatementCell
 */
export function describeCell(cell) {
  if (!cell) return { kind: 'heading', text: null, plusMinus: null, ipotesiZero: false, nonRiconciliato: false };
  const flags = { ipotesiZero: Boolean(cell.ipotesiZero), nonRiconciliato: Boolean(cell.nonRiconciliato) };
  if (cell.origin === 'unknown' || cell.value === null || cell.value === undefined) {
    return { kind: cell.nil ? 'nil' : 'unknown', text: null, plusMinus: null, ...flags };
  }
  const text = formatMoney(cell.value);
  if (text === null) return { kind: 'unknown', text: null, plusMinus: null, ...flags };
  if (cell.origin === 'solved') {
    const exact = !cell.tolerance || decimalSign(cell.tolerance) === 0;
    return { kind: 'solved', text, plusMinus: exact ? null : formatMoney(cell.tolerance), ...flags };
  }
  return { kind: cell.origin === 'computed' ? 'computed' : 'reported', text, plusMinus: null, ...flags };
}

/** Rientro di una riga del prospetto secondo la profondità, contenuto sui telefoni dal CSS. */
export function indentLevel(depth) {
  return Math.max(0, Math.min(6, Number.isInteger(depth) ? depth : 0));
}

const isUnreported = (cell) => describeCell(cell).kind === 'unknown';

/**
 * Le righe di un prospetto da mostrare. Un bilancio micro omette decine di
 * voci: con `showUnknown` falso si nascondono quelle non esposte in nessuno
 * dei due anni, e i titoli rimasti senza righe sotto. Le voci esposte in un
 * anno solo restano, con «non esposta» nell'altro.
 *
 * @param {object[]} lines BilancioStatementLine, nell'ordine del prospetto
 * @param {boolean} showUnknown
 * @returns {{ lines: object[], hidden: number }} `hidden` conta le voci nascoste, non i titoli
 */
export function visibleStatementLines(lines, showUnknown) {
  const list = Array.isArray(lines) ? lines : [];
  if (showUnknown) return { lines: list, hidden: 0 };
  const keep = list.map((line) => line.abstract || !(isUnreported(line.corrente) && isUnreported(line.precedente)));
  const hidden = list.filter((line, index) => !line.abstract && !keep[index]).length;
  // Un titolo resta solo se sotto di lui (fino alla prossima riga allo stesso
  // livello o più in alto) c'è almeno una voce mostrata.
  for (let index = 0; index < list.length; index += 1) {
    if (!list[index].abstract) continue;
    let shown = false;
    for (let next = index + 1; next < list.length && list[next].depth > list[index].depth; next += 1) {
      if (!list[next].abstract && keep[next]) { shown = true; break; }
    }
    keep[index] = shown;
  }
  return { lines: list.filter((line, index) => keep[index]), hidden };
}

// ── Il riscontro con le fatture ───────────────────────────────────────

/**
 * Il colore del badge di ogni esito. Il colore non è mai l'unico segnale: il
 * badge porta sempre l'esito scritto, e accanto c'è la spiegazione.
 */
export const ESITO_BADGE = Object.freeze({
  coerente: 'badge-green',
  da_spiegare: 'badge-blue',
  da_verificare: 'badge-yellow',
  non_calcolabile: 'badge-gray',
});

/**
 * Da che parte va la differenza «sdi meno bilancio», per l'icona che
 * l'accompagna: `up` se le fatture superano il bilancio, `down` se restano
 * sotto, `zero` se coincidono.
 *
 * @returns {'up'|'down'|'zero'|null}
 */
export function differenceDirection(differenza) {
  const sign = decimalSign(differenza);
  if (sign === null) return null;
  return sign > 0 ? 'up' : sign < 0 ? 'down' : 'zero';
}

/** Un documento del riscontro in una riga: tipo, numero e data, per il collegamento alla fattura. */
export function documentKey(doc) {
  return doc?.id ?? `${doc?.tipoDocumento ?? ''}-${doc?.invoiceNumber ?? ''}-${doc?.invoiceDate ?? ''}`;
}

// ── Assunzioni ────────────────────────────────────────────────────────

/** L'aliquota IVA che il server usa se non gliene si chiede un'altra. */
export const DEFAULT_ALIQUOTA_IVA = '22';

/**
 * L'aliquota IVA scritta dall'utente, nella forma che l'API accetta
 * (`^\d{1,3}(\.\d{1,2})?$`, da 0 a 100): «22», «10», «4,5» → «4.5». Null se
 * non è un'aliquota.
 *
 * @param {string|number} input
 * @returns {string|null}
 */
export function parseAliquotaIva(input) {
  const text = String(input ?? '').trim().replace(',', '.');
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(text)) return null;
  if (Number(text) > 100) return null;
  return text.replace(/^0+(?=\d)/, '');
}

// ── Testi del bilancio ────────────────────────────────────────────────

/**
 * Un blocco di testo della nota integrativa in paragrafi. Il server lo manda
 * già come testo semplice (mai HTML); qui si tolgono solo le righe vuote in
 * eccesso, e la pagina lo mostra come testo, mai come markup.
 *
 * @param {string|null|undefined} testo
 * @returns {string[]}
 */
export function textParagraphs(testo) {
  if (typeof testo !== 'string') return [];
  return testo
    .replace(/\r\n?/g, '\n')
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}
